import React, { useState } from 'react';
import { Alert, Modal, TabsProps } from 'antd';

import { DocumentReference } from '@totem/components/documents/types';
import DocumentsLinkPanel from '@totem/components/documents/upload/documentsLinkPanel';
import DocumentsUploadPanel from '@totem/components/documents/upload/documentsUploadPanel';
import LinkToSelector from '@totem/components/documents/upload/linkToSelector';
import WebLinkPanel from '@totem/components/documents/upload/webLinkPanel';
import IBTabs from '@totem/components/ui_components/antDesignOverrides/IBTabs';
import ModalTitle from '@totem/components/ui_components/dialogs/ModalTitle';
import { isNotNull } from '@totem/utilities/common';

type Props = {
  visible: boolean;
  onClose: (refresh: boolean) => void;
  reference?: DocumentReference;
};

const DocumentsMergedDialog = ({ visible, onClose, reference }: Props) => {
  // Opened from a record (building, ticket, sales order...) the document is linked to that record. Opened from
  // the Document Repository there's no record, so the user chooses what to link it to.
  const choosesLink = !isNotNull(reference);
  const [chosenReference, setChosenReference] =
    useState<DocumentReference | null>(null);
  const [linkComplete, setLinkComplete] = useState<boolean>(true);
  const activeReference = choosesLink ? chosenReference : reference;

  const getTabItems = (): TabsProps['items'] => {
    const tabItems: TabsProps['items'] = [
      {
        key: 'Upload',
        label: 'Upload New Document',
        children: (
          <DocumentsUploadPanel
            onClose={onClose}
            reference={activeReference ?? undefined}
          />
        ),
      },
      {
        key: 'Web Link',
        label: 'Add Web Link',
        children: (
          <WebLinkPanel
            onClose={onClose}
            reference={activeReference ?? undefined}
          />
        ),
      },
    ];

    if (isNotNull(reference)) {
      tabItems.push({
        key: 'Link',
        label: 'Link Existing Document',
        children: (
          <DocumentsLinkPanel onClose={onClose} reference={reference} />
        ),
      });
    }

    return tabItems;
  };

  return (
    <Modal
      open={visible}
      onCancel={() => onClose(false)}
      title={
        <ModalTitle>{choosesLink ? 'Add document' : 'Document'}</ModalTitle>
      }
      confirmLoading={false}
      width={640}
      footer={null}
    >
      {choosesLink && (
        <LinkToSelector
          onChange={(nextReference, complete) => {
            setChosenReference(nextReference);

            setLinkComplete(complete);
          }}
        />
      )}
      {linkComplete ? (
        <IBTabs defaultActiveKey={getTabItems()[0].key} items={getTabItems()} />
      ) : (
        <Alert
          type="info"
          showIcon
          message="Choose what to link the document to, then add the file or web link."
        />
      )}
    </Modal>
  );
};

export default DocumentsMergedDialog;
